import styles from './Overlay.module.scss';
import LevelMenu from '@components/components/Navigation/components/MobileNavigation/components/LevelMenu/LevelMenu';
import MobileMenu from '@components/components/Navigation/components/MobileNavigation/components/MobileMenu/MobileMenu';
import { MetaHeaderMobile } from '@components/components/Navigation/components/MetaHeader/MetaHeader';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { usePreventScroll } from '@wienenergiegithub/ui-next';
import { useTabStore } from '@ariakit/react';

// check what meta item to select depending on rootLevel slug
const getSelectedMetaNavigationItem = (metaItems, rootLevel) => {
  const foundItem = metaItems?.find(item => item.url.includes(rootLevel.slug));

  return foundItem ? foundItem.title : metaItems?.[0]?.title;
};

// recurse on menu items array of object until specified level has been reached
// (or no child items available as fallback)
const traverseArrayUntilSpecifiedLevel = (
  items,
  levels,
  selectedIndex = [],
  currentLevel = 0,
) => {
  const levelIndex = selectedIndex?.[currentLevel];
  if (currentLevel === levels) {
    return items?.[levelIndex];
  }
  const childItems = items?.[levelIndex]?.children;

  if (!childItems?.length) {
    return items?.[levelIndex];
  }

  return traverseArrayUntilSpecifiedLevel(
    childItems,
    levels,
    selectedIndex,
    currentLevel + 1,
  );
};

const LevelSwitcher = ({
  items,
  level,
  isReduced,
  selectedIndices,
  setShowOverlay,
  makeLevelDecision,
  enableSmoothTransition,
}) => {
  const sliderRef = useRef(null);
  // show fading background for scrollable content
  const [displayFadingBackground, setDisplayFadingBackground] = useState(false);

  useEffect(() => {
    if (sliderRef.current) {
      // scroll to very top when switching between levels
      sliderRef.current.scrollTo({ top: 0 });
    }
  }, [selectedIndices]);

  return (
    <div
      className={classNames(styles.sliderWrapper, {
        [styles.displayFadingBackground]: displayFadingBackground,
      })}
      ref={sliderRef}
    >
      <div
        className={classNames(styles.slider, {
          [styles.noSmoothTransition]: !enableSmoothTransition,
        })}
        // Going to the next slide means a horizontal translation by 100% (size
        // of each slide)
        style={{ transform: `translateX(calc(-100% * ${level}))` }}
      >
        {/* This ensures that four instances of the level menu for the regular
        navigation and one for reduced one are being rendered */}
        {Array.from({ length: isReduced ? 1 : 4 }, (_, index) => index).map(
          position => {
            const parentItem = traverseArrayUntilSpecifiedLevel(
              items,
              position,
              selectedIndices,
            );

            return (
              <LevelMenu
                ariaHidden={level !== position}
                ariaLabel={`Menü ${parentItem?.title}`}
                displayFadingBackground={displayFadingBackground}
                isFirstLevel={level === 0}
                key={position}
                level={level}
                makeLevelDecision={makeLevelDecision}
                parentItem={parentItem}
                position={position}
                selectedIndices={selectedIndices}
                setDisplayFadingBackground={setDisplayFadingBackground}
                setShowOverlay={setShowOverlay}
              />
            );
          },
        )}
      </div>
    </div>
  );
};

const Overlay = ({
  items,
  showOverlay,
  setShowOverlay,
  isSearchOpen,
  setIsSearchOpen,
  isReduced,
  metaMenu,
  rootLevel,
  logoLink,
  logoLinkDescription,
  logoType,
}) => {
  usePreventScroll(showOverlay);

  const selectedMetaNavigationItem = getSelectedMetaNavigationItem(
    metaMenu,
    rootLevel,
  );
  const [level, setLevel] = useState(0);
  // selection of index at each level from 0 to 4
  const [selectedIndices, setSelectedIndices] = useState([0, 0, 0, 0, 0]);
  const [enableSmoothTransition, setEnableSmoothTransition] = useState(true);

  // change index at specified level and switch to specified level
  const makeLevelDecision = (selectedLevel, selectedIndex) => {
    setSelectedIndices(prev => {
      const copy = [...prev];

      copy[selectedLevel] = selectedIndex;

      return copy;
    });

    setLevel(selectedLevel);
  };

  const [activeId, setActiveId] = useState(selectedMetaNavigationItem);
  const tab = useTabStore({ activeId, setActiveId });

  useEffect(() => {
    let metaIndex = 0;

    if (Array.isArray(metaMenu)) {
      // extract number from currentId
      metaIndex = metaMenu.findIndex(({ title }) => title === activeId);

      if (metaIndex === -1) {
        metaIndex = 0;
      }
    }

    // switch to selected meta navigation item without smooth transition
    setEnableSmoothTransition(false);
    setLevel(0);
    setSelectedIndices([metaIndex, 0, 0, 0, 0]);
    setEnableSmoothTransition(true);
  }, [activeId]);

  const isBusiness = useMemo(() => activeId === 'Business', [activeId]);

  return (
    <div
      aria-modal={showOverlay}
      className={classNames(styles.overlay, {
        [styles.overlayIsShown]: showOverlay,
        [styles.businessBackground]: isBusiness,
      })}
      role="dialog"
    >
      <nav aria-label="Menüleiste" className={styles.overlayNavigation}>
        <MobileMenu
          isReduced={isReduced}
          isSearchOpen={isSearchOpen}
          logoLink={logoLink}
          logoLinkDescription={logoLinkDescription}
          logoType={logoType}
          setIsSearchOpen={setIsSearchOpen}
          setShowOverlay={setShowOverlay}
          showOverlay={showOverlay}
        />
      </nav>
      <div
        className={classNames(styles.mainNavigationContainer, {
          [styles.isBurgerMenuOpen]: showOverlay,
          [styles.isReduced]: isReduced,
        })}
      >
        {!isReduced && <MetaHeaderMobile tab={tab} />}
        <LevelSwitcher
          enableSmoothTransition={enableSmoothTransition}
          isReduced={isReduced}
          items={items}
          level={level}
          makeLevelDecision={makeLevelDecision}
          rootLevel={rootLevel}
          selectedIndices={selectedIndices}
          setShowOverlay={setShowOverlay}
        />
      </div>
    </div>
  );
};

export default Overlay;
